import { type ReactNode, useContext, useMemo } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { isHotelPackage } from 'lib/offer/offerTypes'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Pill from 'LuxKit/Pill'
import { SVG_ICON_SIZE_CSS_VAR } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import PackageUpgradeRoomDetailsExpander from './PackageUpgradeRoomDetailsExpander'
import usePackageUpgradeInclusions from './usePackageUpgradeInclusions'
import { highlightIcons, highlightMessages } from './stayUpgradeModalHighlights'
import {
  getStayUpgradeHighlights,
  getStayUpgradeInclusions,
  getStayUpgradeType,
} from './stayUpgradeModalUtils'
import { getShouldQualifyOfferHotelCancellation } from 'selectors/hotelCancellationSelectors'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'

const EMPTY_INCLUSIONS: Array<App.PackageInclusion> = []

const messages = defineMessages({
  allInclusionsNewBadge: {
    id: '7JqqeQNYLt5yWdxkVy8u',
    defaultMessage: '{count} new total',
    description: 'Inclusion badge when every inclusion in the refreshed mobile room upgrade modal is new',
  },
  someInclusionsNewBadge: {
    id: 'ERpKlsVToNt-S5J7YOvu',
    defaultMessage: '{count} new',
    description: 'Inclusion badge when some inclusions in the refreshed mobile room upgrade modal are new',
  },
  noNewInclusionsBadge: {
    id: 'xPMVS1WOsGQGionqu6Td',
    defaultMessage: '{count} total',
    description: 'Inclusion badge when no inclusions in the refreshed mobile room upgrade modal are new',
  },
  inclusionsHeading: {
    id: '7o8kZY3ghVX0GXwOXB5S',
    defaultMessage: 'Your upgrade includes',
    description: 'Inclusions heading shared by every upgrade type in the refreshed room upgrade modal',
  },
  packageOnlyTitle: {
    id: 'xqFVXVDwyxNmvRQLHrch',
    defaultMessage: 'Get more from your stay',
    description: 'Title for a package-only upgrade in the refreshed mobile room upgrade modal',
  },
  roomUpgradeTitle: {
    id: 'fLeRs6Bs8vno765AyvPw',
    defaultMessage: 'Upgrade your stay',
    description: 'Title for a room upgrade in the refreshed mobile room upgrade modal',
  },
  upgradeButton: {
    id: '5tLaz2DuFnZe0phmbJ7f',
    defaultMessage: 'Upgrade for only {formattedUpgradePrice}',
    description: 'Primary CTA with price difference in the refreshed mobile room upgrade modal',
  },
  noThanksButton: {
    id: 'ILbJM4W8qjEJuMXasEle',
    defaultMessage: 'No thanks',
    description: 'Decline action in the refreshed mobile room upgrade modal',
  },
})

const imageProps: ImageParams = {
  mobileAspectRatio: '3:2',
  quality: 'good',
}

const desktopImageProps: ImageParams = {
  mobileAspectRatio: '3:2',
  tabletAspectRatio: '56:23',
  quality: 'good',
}

const VariantModalContent = styled(ModalContent)`
  ${mediaQueryUp.tablet} {
    --modal-content-gutter: ${rem(20)};
    --modal-content-vertical-gutter: ${rem(20)};
  }
`

const HighlightIcon = styled.div`
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.default.six};
  border-radius: 50%;
  display: flex;
  flex: 0 0 ${rem(40)};
  height: ${rem(40)};
  justify-content: center;
  width: ${rem(40)};

  ${mediaQueryUp.tablet} {
    ${SVG_ICON_SIZE_CSS_VAR}: 24px;
    flex-basis: 40px;
    height: 40px;
    width: 40px;
  }
`

const Highlights = styled.div`
  display: grid;
  gap: ${rem(12)};
  grid-template-columns: minmax(0, 1fr);

  ${mediaQueryUp.tablet} {
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const HighlightItem = styled.div`
  align-items: center;
  display: flex;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: 12px;
  }
`

const HighlightText = styled.div`
  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`

function renderStrongText(chunks: ReactNode): ReactNode {
  return <strong>{chunks}</strong>
}

function VariantSectionTitle({ children, isDesktop }: { children: ReactNode; isDesktop: boolean }) {
  if (isDesktop) return <BodyText variant="large" weight="bold">{children}</BodyText>
  return <Heading variant="heading4">{children}</Heading>
}

function HighlightCopy({ description, isDesktop, title }: { description: string; isDesktop: boolean; title: string }) {
  if (isDesktop) {
    return <HighlightText>
      <BodyText variant="medium" weight="bold">{title}</BodyText>
      <BodyText variant="small" colour="neutral-two">{description}</BodyText>
    </HighlightText>
  }

  return <HighlightText>
    <Heading variant="heading6">{title}</Heading>
    <BodyText variant="medium" colour="neutral-two">{description}</BodyText>
  </HighlightText>
}

interface Props {
  offer: App.HotelOffer | App.VillaOffer
  basePackage: App.Package
  upgradePackage: App.Package
  displayMode: 'mobile' | 'desktop'
  formattedUpgradePrice: string
  formattedUpgradeAddedValue?: string
  isUltraLux: boolean
  isDismissible: boolean
  checkIn?: string
  charterBundleRoute?: App.CharterBundleRoute
  hasFeederFlightIncluded: boolean
  propertyTimezoneOffset?: number
  loyaltyPointsContent?: ReactNode
  luxPlusInclusionsContent?: ReactNode
  onUpgradeClick: () => void
  onNoThanksClick: () => void
}

function StayUpgradeModalVariant(props: Props) {
  const {
    offer,
    basePackage,
    upgradePackage,
    displayMode,
    formattedUpgradePrice,
    formattedUpgradeAddedValue,
    isUltraLux,
    isDismissible,
    checkIn,
    charterBundleRoute,
    hasFeederFlightIncluded,
    propertyTimezoneOffset,
    loyaltyPointsContent,
    luxPlusInclusionsContent,
    onUpgradeClick,
    onNoThanksClick,
  } = props
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const { airport, fareClass, rooms } = offerPageState
  const { inclusions: baseInclusions, bonusInclusions: baseBonusInclusions } = usePackageUpgradeInclusions(basePackage, {
    flightOrigin: airport,
    hasFeederFlightIncluded,
  })
  const { inclusions: upgradeInclusions, bonusInclusions: upgradeBonusInclusions } = usePackageUpgradeInclusions(upgradePackage, {
    flightOrigin: airport,
    hasFeederFlightIncluded,
  })
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: upgradePackage.duration,
    charterBundleRoute,
    rooms,
    fareClass,
    feederOriginAirportCode: airport?.code,
  })
  const baseStayUpgradeInclusions = useMemo(
    () => [...baseInclusions, ...baseBonusInclusions],
    [baseBonusInclusions, baseInclusions],
  )
  const upgradeStayUpgradeInclusions = useMemo(
    () => [...upgradeInclusions, ...upgradeBonusInclusions],
    [upgradeBonusInclusions, upgradeInclusions],
  )
  const stayUpgradeInclusions = useMemo(
    () => getStayUpgradeInclusions(baseStayUpgradeInclusions, upgradeStayUpgradeInclusions),
    [baseStayUpgradeInclusions, upgradeStayUpgradeInclusions],
  )
  const upgradeType = useMemo(
    () => getStayUpgradeType(basePackage, upgradePackage, stayUpgradeInclusions),
    [basePackage, stayUpgradeInclusions, upgradePackage],
  )
  const highlights = useMemo(() => {
    if (upgradeType === 'packageOnly') return []

    return getStayUpgradeHighlights(
      basePackage,
      upgradePackage,
      baseStayUpgradeInclusions,
      upgradeStayUpgradeInclusions,
    )
  }, [basePackage, baseStayUpgradeInclusions, upgradePackage, upgradeStayUpgradeInclusions, upgradeType])
  const upgradeHotelPackage = isHotelPackage(upgradePackage) ? upgradePackage : undefined
  const roomType = upgradeHotelPackage?.roomType
  const isDesktop = displayMode === 'desktop'
  const isPackageOnly = upgradeType === 'packageOnly'
  const hasRoomDetails = !isPackageOnly && !!roomType && (
    !!roomType.sizeSqm ||
    !!roomType.bedGroups.length ||
    !!roomType.numberOfBedrooms
  )
  const inclusionBadge = useMemo(() => {
    if (stayUpgradeInclusions.newCount === stayUpgradeInclusions.totalCount) {
      return intl.formatMessage(messages.allInclusionsNewBadge, { count: stayUpgradeInclusions.totalCount })
    }
    if (stayUpgradeInclusions.newCount > 0) {
      return intl.formatMessage(messages.someInclusionsNewBadge, { count: stayUpgradeInclusions.newCount })
    }
    return intl.formatMessage(messages.noNewInclusionsBadge, { count: stayUpgradeInclusions.totalCount })
  }, [intl, stayUpgradeInclusions.newCount, stayUpgradeInclusions.totalCount])

  const inclusionHeading = intl.formatMessage(messages.inclusionsHeading)

  const displayedInclusions = useMemo(() => stayUpgradeInclusions.items.map(({ inclusion, isNew }) => ({
    ...inclusion,
    isHighlighted: isNew,
  })), [stayUpgradeInclusions.items])

  const modalTitle = intl.formatMessage(isPackageOnly ? messages.packageOnlyTitle : messages.roomUpgradeTitle)

  let upgradeSubtitle: ReactNode
  if (isPackageOnly) {
    upgradeSubtitle = <BodyText variant="large">{upgradePackage.name}</BodyText>
  } else {
    upgradeSubtitle = <BodyText variant="large">
      <FormattedMessage
        id="j8n2yYoBunNIliV55mvI"
        defaultMessage="{baseRoomName} → <strong>{upgradeRoomName}</strong>"
        description="Selected-to-suggested room transition in the refreshed mobile room upgrade modal"
        values={{
          baseRoomName: basePackage.roomType?.name,
          upgradeRoomName: upgradePackage.roomType?.name,
          strong: renderStrongText,
        }}
      />
    </BodyText>
  }

  const upgradeButtonLabel = intl.formatMessage(messages.upgradeButton, { formattedUpgradePrice })
  const noThanksButtonLabel = intl.formatMessage(messages.noThanksButton)
  const primaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    onClick: onUpgradeClick,
    palette: isUltraLux ? 'ultralux' : undefined,
    children: upgradeButtonLabel,
  }), [isUltraLux, onUpgradeClick, upgradeButtonLabel])
  const secondaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    onClick: onNoThanksClick,
    children: noThanksButtonLabel,
  }), [noThanksButtonLabel, onNoThanksClick])

  return <ModalBase size={isDesktop ? 'S' : 'L'} height={isDesktop ? 'auto' : 'full'} dismissible={isDismissible}>
    <ModalHeader title="" isOverlay dismissible={isDismissible} />
    <ModalBody>
      {!!roomType?.images.length && <ImageGallery
        images={roomType.images}
        imageParams={isDesktop ? desktopImageProps : imageProps}
        size={isDesktop ? 'small' : 'large'}
      />}
      <VariantModalContent>
        <VerticalSpacer gap={20}>
          <VerticalSpacer gap={8}>
            <Heading variant={isDesktop ? 'heading3' : 'heading2'}>{modalTitle}</Heading>
            {upgradeSubtitle}
            {upgradePackage.roomRate && <FlashCancellationPolicies
              checkInDate={checkIn}
              charterDepartDate={charterBundleRoute ? charterDepartDate : undefined}
              propertyTimezoneOffset={propertyTimezoneOffset}
              cancellationPolicy={upgradePackage.roomRate.cancellationPolicy}
              showHotelOnly={getShouldQualifyOfferHotelCancellation(offerPageState) || hasAirportTransferPackageInclusions(upgradePackage)}
            />}

            {loyaltyPointsContent}
          </VerticalSpacer>

          {!!highlights.length && <Highlights>
            {highlights.map(highlight => {
              const Highlight = highlightIcons[highlight.id]
              return <HighlightItem key={highlight.id}>
                <HighlightIcon><Highlight aria-hidden="true" /></HighlightIcon>
                <HighlightCopy
                  isDesktop={isDesktop}
                  title={intl.formatMessage(highlightMessages[`${highlight.id}-title`])}
                  description={intl.formatMessage(highlightMessages[`${highlight.id}-description`])}
                />
              </HighlightItem>
            })}
          </Highlights>}

          {hasRoomDetails && upgradeHotelPackage && <VerticalSpacer gap={8}>
            <VariantSectionTitle isDesktop={isDesktop}><FormattedMessage
              id="MtIwTaNyWemwPyVQ7CTB"
              defaultMessage="Upgraded room details"
              description="Heading above room detail chips in the refreshed mobile room upgrade modal"
            />
            </VariantSectionTitle>
            <AccommodationRoomDetailsList
              roomPackage={upgradeHotelPackage}
              shouldShowAmenitiesAndPoliciesCta={false}
            />
          </VerticalSpacer>}

          {luxPlusInclusionsContent}

          {!!displayedInclusions.length && <VerticalSpacer gap={8}>
            <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign={isDesktop ? 'start' : 'space-between'}>
              <VariantSectionTitle isDesktop={isDesktop}>{inclusionHeading}</VariantSectionTitle>
              <Pill palette="success">{inclusionBadge}</Pill>
            </Group>
            <InclusionsSection offer={offer} inclusions={displayedInclusions} bonusInclusions={EMPTY_INCLUSIONS} size="medium" />
          </VerticalSpacer>}

          {roomType && isDesktop && <PackageUpgradeRoomDetailsExpander roomType={roomType} size="medium" />}
          {roomType && !isDesktop && <LePackageUpgradeCardRoomDetails roomType={roomType} size="medium" />}
        </VerticalSpacer>
      </VariantModalContent>
    </ModalBody>
    <ModalFooter
      actionsLayout={isDesktop ? 'auto' : 'stacked'}
      primaryActionProps={primaryActionProps}
      primaryActionSupplement={formattedUpgradeAddedValue && <BodyText variant="small" colour="neutral-two">
        <FormattedMessage
          id="ZbcZLBc2RmUPc60vFbfa"
          defaultMessage="To unlock an extra {formattedUpgradeAddedValue} of value"
          description="Value callout beneath the upgrade CTA in the refreshed room upgrade modal"
          values={{ formattedUpgradeAddedValue }}
        />
      </BodyText>}
      secondaryActionProps={secondaryActionProps}
    />
  </ModalBase>
}

export default StayUpgradeModalVariant
